import React, { useState } from 'react';
import { useForceLightTheme } from '../contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  useForceLightTheme();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('ff_remember_user'));
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('ff_remember_user');
    return { username: saved || '', password: '' };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(formData.username, formData.password);
      if (rememberMe) localStorage.setItem('ff_remember_user', formData.username);
      else localStorage.removeItem('ff_remember_user');
      toast.success('Welcome back!');
      navigate(user.profile_complete ? '/social' : '/profile/setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" style={{ background: 'var(--club-cream)' }} data-testid="login-page">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img
            src="/logo-the-club.png"
            alt="The Club"
            className="h-24 w-auto mb-6"
            data-testid="login-logo"
          />
          <h1 className="font-serif text-3xl font-semibold tracking-tight" style={{ color: 'var(--deep-forest)' }}>
            Welcome Back
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--warm-gray)' }}>
            Sign in to your account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="username" className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>
              Username
            </Label>
            <Input
              id="username"
              type="text"
              placeholder="Username or email"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              className="h-12 rounded-lg border bg-white/80 focus:bg-white transition-colors"
              style={{ borderColor: 'var(--fairway-sand)', color: '#000', WebkitTextFillColor: '#000' }}
              data-testid="username-input"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>
                Password
              </Label>
              <Link to="/forgot-password" className="text-xs font-medium hover:underline" style={{ color: 'var(--club-green)' }} data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-12 rounded-lg border bg-white/80 focus:bg-white transition-colors pr-10"
                style={{ borderColor: 'var(--fairway-sand)', color: '#000', WebkitTextFillColor: '#000' }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-70 transition-opacity"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded accent-[var(--club-green)] cursor-pointer"
              data-testid="remember-me-checkbox"
            />
            <Label htmlFor="rememberMe" className="text-sm cursor-pointer select-none" style={{ color: 'var(--warm-gray)' }}>
              Remember me
            </Label>
          </div>

          <Button
            type="submit"
            className="w-full h-12 rounded-lg font-semibold text-sm tracking-wide btn-press transition-all"
            style={{ background: 'var(--club-green)', color: '#fff' }}
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-8">
          <div className="flex-1 h-px" style={{ background: 'var(--fairway-sand)' }} />
          <span className="text-xs" style={{ color: 'var(--soft-taupe)' }}>or</span>
          <div className="flex-1 h-px" style={{ background: 'var(--fairway-sand)' }} />
        </div>

        <p className="text-center text-sm" style={{ color: 'var(--warm-gray)' }}>
          Don't have an account?{' '}
          <Link to="/register" className="font-semibold hover:underline" style={{ color: 'var(--club-green)' }} data-testid="register-link">
            Create Account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
